import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Alert, LoadingBlock, Spinner } from '../components/ui';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../components/Field';
import { formatDateTime, isoToLocalInput, localInputToIso } from '../lib/format';
import type { AdminEvent, EventCategory } from '../lib/types';

interface FormState {
  name: string;
  tagline: string;
  description: string;
  eventDate: string;
  startTime: string;
  timezone: string;
  venue: string;
  venueAddress: string;
  distanceMeters: string;
  laneCount: string;
  seedingMode: 'lanes' | 'waves';
  heatDurationMinutes: string;
  changeoverMinutes: string;
  maxEntries: string;
  minAge: string;
  entryFeePounds: string;
  currency: string;
  registrationOpensAt: string;
  registrationClosesAt: string;
  contactEmail: string;
  paymentMessage: string;
  minSeedMinutes: string;
  maxSeedMinutes: string;
  requireMedicalDeclaration: boolean;
  heatsLocked: boolean;
  categories: EventCategory[];
}

function toForm(e: AdminEvent): FormState {
  return {
    name: e.name,
    tagline: e.tagline,
    description: e.description,
    eventDate: e.eventDate,
    startTime: e.startTime,
    timezone: e.timezone,
    venue: e.venue,
    venueAddress: e.venueAddress,
    distanceMeters: String(e.distanceMeters),
    laneCount: String(e.laneCount),
    seedingMode: e.seedingMode,
    heatDurationMinutes: String(e.heatDurationMinutes),
    changeoverMinutes: String(e.changeoverMinutes),
    maxEntries: String(e.maxEntries),
    minAge: String(e.minAge),
    entryFeePounds: (e.entryFeePence / 100).toFixed(2),
    currency: e.currency,
    registrationOpensAt: isoToLocalInput(e.registrationOpensAt),
    registrationClosesAt: isoToLocalInput(e.registrationClosesAt),
    contactEmail: e.contactEmail,
    paymentMessage: e.paymentMessage,
    minSeedMinutes: String(Math.round(e.minSeedTimeMs / 60000)),
    maxSeedMinutes: String(Math.round(e.maxSeedTimeMs / 60000)),
    requireMedicalDeclaration: e.requireMedicalDeclaration,
    heatsLocked: e.heatsLocked,
    categories: e.categories.map((c) => ({ ...c })),
  };
}

export function SettingsPage() {
  const event = useAsync<AdminEvent>((signal) => api.admin.getEvent(signal), []);
  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event.data) setForm(toForm(event.data));
  }, [event.data]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function updateCategory(index: number, patch: Partial<EventCategory>) {
    setForm((prev) => {
      if (!prev) return prev;
      const categories = prev.categories.map((c, i) => (i === index ? { ...c, ...patch } : c));
      return { ...prev, categories };
    });
  }

  function addCategory() {
    setForm((prev) =>
      prev
        ? {
            ...prev,
            categories: [...prev.categories, { id: '', label: '', seedSeparately: true }],
          }
        : prev,
    );
  }

  function removeCategory(index: number) {
    setForm((prev) =>
      prev ? { ...prev, categories: prev.categories.filter((_, i) => i !== index) } : prev,
    );
  }

  function validate(f: FormState): Record<string, string> {
    const found: Record<string, string> = {};
    if (!f.name.trim()) found['name'] = 'Required';
    if (!f.eventDate) found['eventDate'] = 'Required';
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(f.startTime)) found['startTime'] = 'Use HH:MM';
    if (!/^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(f.contactEmail.trim())) {
      found['contactEmail'] = 'Enter a valid email address';
    }
    if (!(Number(f.maxEntries) >= 1)) found['maxEntries'] = 'Must be at least 1';
    if (!(Number(f.laneCount) >= 1)) found['laneCount'] = 'Must be at least 1';
    if (!(Number(f.heatDurationMinutes) >= 1)) found['heatDurationMinutes'] = 'Must be at least 1';
    if (!f.changeoverMinutes.trim()) {
      found['changeoverMinutes'] = 'Required - enter 0 for back-to-back heats';
    } else if (!(Number(f.changeoverMinutes) >= 0)) {
      found['changeoverMinutes'] = 'Cannot be negative';
    }
    if (!(Number(f.distanceMeters) >= 25)) found['distanceMeters'] = 'Must be at least 25';
    if (!(Number(f.entryFeePounds) >= 0)) found['entryFeePounds'] = 'Cannot be negative';
    if (f.currency.trim().length !== 3) found['currency'] = 'Use a 3-letter code, e.g. GBP';

    const minMinutes = Number(f.minSeedMinutes);
    const maxMinutes = Number(f.maxSeedMinutes);
    if (!(minMinutes > 0)) found['minSeedMinutes'] = 'Must be greater than 0';
    if (!(maxMinutes > 0)) found['maxSeedMinutes'] = 'Must be greater than 0';
    if (minMinutes >= maxMinutes) {
      found['minSeedMinutes'] = 'Fastest permitted time must be less than the cut-off';
    }

    if (!f.registrationOpensAt) found['registrationOpensAt'] = 'Required';
    if (!f.registrationClosesAt) found['registrationClosesAt'] = 'Required';
    if (
      f.registrationOpensAt &&
      f.registrationClosesAt &&
      f.registrationOpensAt >= f.registrationClosesAt
    ) {
      found['registrationClosesAt'] = 'Must be after entries open';
    }

    if (f.categories.length === 0) found['categories'] = 'Add at least one category';
    const ids = new Set<string>();
    for (const category of f.categories) {
      if (!/^[a-z0-9][a-z0-9_-]{0,48}$/.test(category.id)) {
        found['categories'] = 'Category ids must be lowercase letters, numbers, dashes or underscores';
        break;
      }
      if (!category.label.trim()) {
        found['categories'] = 'Every category needs a label';
        break;
      }
      if (ids.has(category.id)) {
        found['categories'] = `Duplicate category id: ${category.id}`;
        break;
      }
      ids.add(category.id);
    }

    return found;
  }

  async function onSubmit(formEvent: React.FormEvent) {
    formEvent.preventDefault();
    if (!form) return;

    const found = validate(form);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setFormError('Please correct the highlighted fields.');
      return;
    }

    setSaving(true);
    setErrors({});
    setFormError(null);
    setNotice(null);

    try {
      const updated = await api.admin.updateEvent({
        name: form.name.trim(),
        tagline: form.tagline.trim(),
        description: form.description.trim(),
        eventDate: form.eventDate,
        startTime: form.startTime,
        timezone: form.timezone.trim(),
        venue: form.venue.trim(),
        venueAddress: form.venueAddress.trim(),
        distanceMeters: Number(form.distanceMeters),
        laneCount: Number(form.laneCount),
        seedingMode: form.seedingMode,
        heatDurationMinutes: Number(form.heatDurationMinutes),
        changeoverMinutes: Number(form.changeoverMinutes),
        maxEntries: Number(form.maxEntries),
        minAge: Number(form.minAge),
        entryFeePence: Math.round(Number(form.entryFeePounds) * 100),
        currency: form.currency.trim().toUpperCase(),
        registrationOpensAt: localInputToIso(form.registrationOpensAt),
        registrationClosesAt: localInputToIso(form.registrationClosesAt),
        contactEmail: form.contactEmail.trim(),
        paymentMessage: form.paymentMessage.trim(),
        categories: form.categories.map((c) => ({
          id: c.id.trim(),
          label: c.label.trim(),
          seedSeparately: c.seedSeparately,
        })),
        minSeedTimeMs: Number(form.minSeedMinutes) * 60000,
        maxSeedTimeMs: Number(form.maxSeedMinutes) * 60000,
        requireMedicalDeclaration: form.requireMedicalDeclaration,
        heatsLocked: form.heatsLocked,
      });
      setForm(toForm(updated));
      setNotice('Settings saved.');
      event.setData(updated);
      window.scrollTo({ top: 0 });
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        setErrors(err.fields);
      } else {
        setFormError('Could not save the settings. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (event.loading || !form) return <LoadingBlock message="Loading settings..." />;
  if (event.error || !event.data) {
    return (
      <Alert variant="error" title="Could not load settings">
        <p>{event.error ?? 'Please try again.'}</p>
      </Alert>
    );
  }

  const unit = form.seedingMode === 'waves' ? 'wave' : 'heat';
  const unitPlural = `${unit}s`;

  return (
    <div className="stack">
      <div className="page-header">
        <div>
          <h1>Event settings</h1>
          <p>Last updated {formatDateTime(event.data.updatedAt)}</p>
        </div>
      </div>

      {notice && (
        <Alert variant="success">
          <p style={{ marginBottom: 0 }}>{notice}</p>
        </Alert>
      )}
      {formError && (
        <Alert variant="error" title="Not saved">
          <p style={{ marginBottom: 0 }}>{formError}</p>
        </Alert>
      )}

      <form className="stack" onSubmit={onSubmit} noValidate>
        <section className="card stack">
          <h2>Event details</h2>
          <div className="form-grid">
            <TextField
              label="Event name"
              required
              wide
              value={form.name}
              error={errors['name']}
              onChange={(e) => set('name', e.target.value)}
            />
            <TextField
              label="Tagline"
              wide
              value={form.tagline}
              error={errors['tagline']}
              onChange={(e) => set('tagline', e.target.value)}
            />
          </div>
          <TextAreaField
            label="Description"
            hint="Shown on the public home page."
            value={form.description}
            error={errors['description']}
            maxLength={4000}
            onChange={(e) => set('description', e.target.value)}
          />
          <div className="form-grid">
            <TextField
              label="Event date"
              type="date"
              required
              value={form.eventDate}
              error={errors['eventDate']}
              onChange={(e) => set('eventDate', e.target.value)}
            />
            <TextField
              label="First start time"
              type="time"
              required
              value={form.startTime}
              error={errors['startTime']}
              onChange={(e) => set('startTime', e.target.value)}
            />
            <TextField
              label="Timezone label"
              hint="Display only, e.g. Europe/London."
              value={form.timezone}
              error={errors['timezone']}
              onChange={(e) => set('timezone', e.target.value)}
            />
            <TextField
              label="Venue"
              value={form.venue}
              error={errors['venue']}
              onChange={(e) => set('venue', e.target.value)}
            />
            <TextField
              label="Venue address"
              wide
              value={form.venueAddress}
              error={errors['venueAddress']}
              onChange={(e) => set('venueAddress', e.target.value)}
            />
            <TextField
              label="Contact email"
              type="email"
              required
              value={form.contactEmail}
              error={errors['contactEmail']}
              onChange={(e) => set('contactEmail', e.target.value)}
            />
          </div>
        </section>

        <section className="card stack">
          <h2>Entries</h2>
          <div className="form-grid">
            <TextField
              label="Distance (metres)"
              type="number"
              min={25}
              required
              value={form.distanceMeters}
              error={errors['distanceMeters']}
              onChange={(e) => set('distanceMeters', e.target.value)}
            />
            <TextField
              label="Maximum entries"
              type="number"
              min={1}
              required
              value={form.maxEntries}
              error={errors['maxEntries']}
              onChange={(e) => set('maxEntries', e.target.value)}
            />
            <TextField
              label="Minimum age on event day"
              type="number"
              min={0}
              max={100}
              value={form.minAge}
              error={errors['minAge']}
              onChange={(e) => set('minAge', e.target.value)}
            />
            <TextField
              label="Entry fee"
              type="number"
              min={0}
              step={0.01}
              hint="In major units, e.g. 35.00"
              value={form.entryFeePounds}
              error={errors['entryFeePounds']}
              onChange={(e) => set('entryFeePounds', e.target.value)}
            />
            <TextField
              label="Currency code"
              maxLength={3}
              value={form.currency}
              error={errors['currency']}
              onChange={(e) => set('currency', e.target.value.toUpperCase())}
            />
            <TextField
              label="Entries open (UTC)"
              type="datetime-local"
              required
              value={form.registrationOpensAt}
              error={errors['registrationOpensAt']}
              onChange={(e) => set('registrationOpensAt', e.target.value)}
            />
            <TextField
              label="Entries close (UTC)"
              type="datetime-local"
              required
              value={form.registrationClosesAt}
              error={errors['registrationClosesAt']}
              onChange={(e) => set('registrationClosesAt', e.target.value)}
            />
          </div>
          <TextAreaField
            label="Payment message"
            hint={
              <>
                Shown to competitors on the confirmation screen and again under Find my entry. Put
                your bank details here, one per line. Write <code>{'{reference}'}</code> where you
                want their entry reference to appear. Leave this blank to keep the standard message.
              </>
            }
            rows={7}
            maxLength={2000}
            value={form.paymentMessage}
            error={errors['paymentMessage']}
            onChange={(e) => set('paymentMessage', e.target.value)}
          />

          <CheckboxField
            label="Require an emergency contact on the entry form"
            checked={form.requireMedicalDeclaration}
            onChange={(checked) => set('requireMedicalDeclaration', checked)}
          />
        </section>

        <section className="card stack">
          <h2>Predicted times and seeding</h2>
          <p className="muted" style={{ marginBottom: 0 }}>
            Entries outside this range are rejected, which catches typos like 2:30 instead of 24:30.
          </p>
          <div className="form-grid">
            <TextField
              label="Fastest permitted (minutes)"
              type="number"
              min={1}
              required
              value={form.minSeedMinutes}
              error={errors['minSeedMinutes']}
              onChange={(e) => set('minSeedMinutes', e.target.value)}
            />
            <TextField
              label="Cut-off time (minutes)"
              type="number"
              min={1}
              required
              value={form.maxSeedMinutes}
              error={errors['maxSeedMinutes']}
              onChange={(e) => set('maxSeedMinutes', e.target.value)}
            />
            <SelectField
              label="Default seeding mode"
              options={[
                { value: 'waves', label: 'Waves (open water)' },
                { value: 'lanes', label: 'Lanes (pool)' },
              ]}
              value={form.seedingMode}
              onChange={(e) => set('seedingMode', e.target.value as 'lanes' | 'waves')}
            />
            <TextField
              label={form.seedingMode === 'waves' ? 'Swimmers per wave' : 'Lanes per heat'}
              type="number"
              min={1}
              max={60}
              required
              value={form.laneCount}
              error={errors['laneCount']}
              onChange={(e) => set('laneCount', e.target.value)}
            />
            <TextField
              label={`Default ${unit} duration (minutes)`}
              hint={`Used when a ${unit} has no usable predicted times. Otherwise each ${unit} is as long as its slowest swimmer, rounded up to 5 minutes. Applies from the next time you generate ${unitPlural}.`}
              type="number"
              min={1}
              max={240}
              value={form.heatDurationMinutes}
              error={errors['heatDurationMinutes']}
              onChange={(e) => set('heatDurationMinutes', e.target.value)}
            />
            <TextField
              label={`Minutes between ${unitPlural} (changeover)`}
              hint={`Clearing time added between every pair of ${unitPlural}. Applies from the next time you generate ${unitPlural}.`}
              type="number"
              min={0}
              max={120}
              value={form.changeoverMinutes}
              error={errors['changeoverMinutes']}
              onChange={(e) => set('changeoverMinutes', e.target.value)}
            />
          </div>

          <CheckboxField
            label="Lock heats (blocks regenerating and manual moves)"
            checked={form.heatsLocked}
            onChange={(checked) => set('heatsLocked', checked)}
          />
        </section>

        <section className="card stack">
          <h2>Categories</h2>
          <p className="muted">
            Competitors choose one of these when they enter. Categories flagged{' '}
            <em>seed separately</em> get their own blocks of heats.
          </p>
          {errors['categories'] && (
            <Alert variant="error">
              <p style={{ marginBottom: 0 }}>{errors['categories']}</p>
            </Alert>
          )}

          {form.categories.map((category, index) => (
            <fieldset key={index}>
              <legend>Category {index + 1}</legend>
              <div className="form-grid">
                <TextField
                  label="Id"
                  hint="Lowercase, no spaces. Used in stored entries; avoid changing it later."
                  value={category.id}
                  onChange={(e) =>
                    updateCategory(index, { id: e.target.value.toLowerCase().replace(/\s+/g, '-') })
                  }
                />
                <TextField
                  label="Label"
                  hint="Shown to competitors."
                  value={category.label}
                  onChange={(e) => updateCategory(index, { label: e.target.value })}
                />
              </div>
              <div className="row" style={{ marginTop: 'var(--space-3)' }}>
                <CheckboxField
                  inline
                  label="Seed separately"
                  checked={category.seedSeparately}
                  onChange={(checked) => updateCategory(index, { seedSeparately: checked })}
                />
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => removeCategory(index)}
                  disabled={form.categories.length <= 1}
                >
                  Remove
                </button>
              </div>
            </fieldset>
          ))}

          <div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addCategory}>
              Add category
            </button>
          </div>
          <p className="faint" style={{ marginBottom: 0 }}>
            A category still in use by an entry cannot be removed; move those entries first.
          </p>
        </section>

        <div className="row row-end">
          <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
            {saving ? (
              <>
                <Spinner label="Saving" /> Saving...
              </>
            ) : (
              'Save settings'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
